/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { TransportRequestTypes } from '../../enums';
import { HttpHeaders, TimeUnitsInSeconds } from '../../../../enums';

import type {
    TransportMiddlewareContext,
    TransportMiddlewareRequest,
    TransportMiddlewareResponse,
    TransportStrategyConfigs,
    TransportStrategyFlows,
} from '../../interfaces';
import type { TransportConfigParams, TransportHeadersParams } from './interfaces';

type Ctx = TransportMiddlewareContext;
type Req<Data> = TransportMiddlewareRequest<Data>;
type Res<SuccessResponse, FailedResponse> = TransportMiddlewareResponse<SuccessResponse, FailedResponse>;

export class TransportConfig {

    protected readonly strategyFlows: TransportStrategyFlows<Ctx, Req<any>, Res<any, any>>;
    protected readonly strategyConfigs: TransportStrategyConfigs;

    protected readonly baseUrl: string;
    protected readonly restPrefix: string;
    protected readonly customBaseHeaders?: Record<string, string> = {};
    protected readonly retryAfterHeaderName: string;
    protected readonly basicDelayFor429InSec: number;
    protected readonly allowedAuthAttempts: number;

    constructor(transportConfigParams?: TransportConfigParams<Ctx, Req<any>, Res<any, any>>) {
        const {
            strategies,
            baseUrl = '',
            restPrefix = '',
            customBaseHeaders = {},
            retryAfterHeaderName = HttpHeaders.RetryAfter,
            basicDelayFor429InSec = TimeUnitsInSeconds.Minute,
            allowedAuthAttempts = 2,
        } = transportConfigParams;
        const strategyEntries = Object.entries(strategies);

        this.baseUrl = baseUrl;
        this.restPrefix = restPrefix;
        this.customBaseHeaders = customBaseHeaders;
        this.retryAfterHeaderName = retryAfterHeaderName;
        this.basicDelayFor429InSec = basicDelayFor429InSec;
        this.allowedAuthAttempts = allowedAuthAttempts;

        this.strategyFlows = Object.fromEntries(strategyEntries.map(([name, strategyData]) => ([name, strategyData.flow])));
        this.strategyConfigs = Object.fromEntries(strategyEntries.map(([name, strategyData]) => ([name, strategyData.config])));
    }

    getStrategyFlows<T, R, E>(): TransportStrategyFlows<Ctx, Req<T>, Res<R, E>> {
        return this.strategyFlows;
    }

    getStrategyConfigs(): TransportStrategyConfigs {
        return this.strategyConfigs;
    }

    getBaseUrl(): string {
        return this.baseUrl;
    }

    getRestPrefix(): string {
        return this.restPrefix;
    }

    getRetryAfterHeaderName(): string {
        return this.retryAfterHeaderName;
    }

    getBasicDelayFor429InSec(): number {
        return this.basicDelayFor429InSec;
    }

    getBaseHeaders(params: TransportHeadersParams): Record<string, string> {
        const { token, type } = params;
        const json = 'application/json';
        const headers: Record<string, string> = {
            [HttpHeaders.Accept]: json,
            [HttpHeaders.ContentType]: json,
            [HttpHeaders.UiRequest]: `${true}`,
            ...(token ? { [HttpHeaders.Authorization]: `Bearer ${token}` } : {}),
        };

        if (type === TransportRequestTypes.ToStreamUploadFile || type === TransportRequestTypes.ToStreamUploadFileRest) {
            headers[HttpHeaders.ContentType] = 'application/octet-stream';
        }

        if (type === TransportRequestTypes.ToUploadFormData) {
            headers[HttpHeaders.ContentType] = undefined;
        }

        return Object.fromEntries(Object.entries({ ...headers, ...this.customBaseHeaders }).filter(entry => entry[1]));
    }

    getAllowedAuthAttempts(): number {
        return this.allowedAuthAttempts;
    }

}
